import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Target, CheckCircle2, XCircle, MinusCircle, Info, Brain, Moon, Droplets, Dumbbell, Flame, Smile } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [30, 60, 90];

const METRIC_META = {
    sleep_hours: { Icon: Moon, color: '#6366f1', de: 'Schlaf', en: 'Sleep' },
    water_ml: { Icon: Droplets, color: '#3b82f6', de: 'Wasser', en: 'Water' },
    workouts_per_day: { Icon: Dumbbell, color: '#22c55e', de: 'Workouts', en: 'Workouts' },
    calories: { Icon: Flame, color: '#f97316', de: 'Kalorien', en: 'Calories' },
    mood_score: { Icon: Smile, color: '#ec4899', de: 'Stimmung', en: 'Mood' },
};

function LearningCurve({ weeks, color = '#22c55e' }) {
    if (!weeks || weeks.length === 0) return null;
    const w = 260, h = 50;
    const pts = weeks.map((wk, i) => {
        const x = (i / Math.max(1, weeks.length - 1)) * w;
        const v = wk.accuracy_pct;
        const y = v == null ? null : h - (v / 100) * (h - 6) - 3;
        return { x, y, v, hits: wk.hits, total: wk.total };
    });
    // segments skipping null points
    const segments = [];
    let cur = [];
    pts.forEach(p => {
        if (p.y == null) {
            if (cur.length > 1) segments.push(cur);
            cur = [];
        } else {
            cur.push(p);
        }
    });
    if (cur.length > 1) segments.push(cur);
    const path = segments.map(seg => 'M ' + seg.map(p => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')).join(' ');
    return (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-12" aria-hidden="true">
            <line x1="0" y1={h - (50/100)*(h-6) - 3} x2={w} y2={h - (50/100)*(h-6) - 3} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="2 2" />
            {path && <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />}
            {pts.map((p, i) => p.y != null && (
                <circle key={i} cx={p.x} cy={p.y} r="2" fill={color}>
                    <title>{`${p.v}% (${p.hits}/${p.total})`}</title>
                </circle>
            ))}
        </svg>
    );
}

export default function PredictionHistory() {
    const { language } = useLanguage();
    const locale = language === 'de' ? deLocale : enUS;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);
    const [hoveredDay, setHoveredDay] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/health/symptoms/prediction-history?days=${days}`);
                setData(res.data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [days]);

    const t = (de, en) => (language === 'de' ? de : en);

    if (loading) {
        return (
            <Card className="animate-pulse" data-testid="prediction-history-loading">
                <CardContent className="h-40" />
            </Card>
        );
    }
    if (!data) return null;

    const { daily, total_predictions, hits, misses, accuracy_pct, learning_curve, per_metric_accuracy, calibration_note } = data;
    const noData = total_predictions === 0 && !daily.some(d => d.predicted);

    return (
        <Card className="border" data-testid="prediction-history">
            <CardContent className="p-4 space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-amber-500/15 flex items-center justify-center">
                            <Target className="w-4 h-4 text-amber-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold">{t('Nanouks Vorhersage-Genauigkeit', "Nanouk's prediction accuracy")}</h3>
                            <p className="text-[10px] text-muted-foreground">
                                {t(`Letzte ${days} Tage`, `Last ${days} days`)}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-1">
                        {RANGES.map(d => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                                    days === d ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'
                                }`}
                                data-testid={`prediction-history-range-${d}`}
                            >
                                {d}T
                            </button>
                        ))}
                    </div>
                </div>

                {noData ? (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3" data-testid="prediction-history-empty">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <p>
                            {t(
                                'Noch keine Vorhersagen aufgezeichnet. Nanouk braucht mind. 3 Vorkommen eines Symptoms in 90 Tagen, um Muster zu erkennen.',
                                'No predictions recorded yet. Nanouk needs at least 3 occurrences of a symptom in 90 days to spot patterns.'
                            )}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Stats row */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center" data-testid="prediction-hits">
                                <p className="text-lg font-bold text-emerald-500 leading-none">{hits}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{t('Treffer', 'Hits')}</p>
                            </div>
                            <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-center" data-testid="prediction-misses">
                                <p className="text-lg font-bold text-rose-500 leading-none">{misses}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{t('Verfehlt', 'Misses')}</p>
                            </div>
                            <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 text-center" data-testid="prediction-accuracy">
                                <p className="text-lg font-bold text-primary leading-none">
                                    {accuracy_pct != null ? `${accuracy_pct}%` : '—'}
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{t('Genauigkeit', 'Accuracy')}</p>
                            </div>
                        </div>

                        {/* Heatmap grid */}
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                                {t('Tages-Heatmap (Treffer / Verfehlt / Keine Vorhersage)', 'Daily heatmap (hit / miss / no prediction)')}
                            </p>
                            <div className="grid grid-flow-col auto-cols-fr gap-1" style={{ gridTemplateRows: 'repeat(7, minmax(0, 1fr))' }}>
                                {daily.map((d, idx) => {
                                    let bgClass = 'bg-muted/40';
                                    let testStatus = 'none';
                                    if (d.predicted && d.is_today) {
                                        bgClass = 'bg-amber-500/40 ring-1 ring-amber-500';
                                        testStatus = 'today';
                                    } else if (d.predicted && d.hit === true) {
                                        bgClass = 'bg-emerald-500';
                                        testStatus = 'hit';
                                    } else if (d.predicted && d.hit === false) {
                                        bgClass = 'bg-rose-500/70';
                                        testStatus = 'miss';
                                    } else if (d.predicted) {
                                        bgClass = 'bg-cyan-500/40';
                                        testStatus = 'pending';
                                    }
                                    let dateStr = d.date;
                                    try { dateStr = format(parseISO(d.date), 'd. MMM', { locale }); } catch (e) { /* ignore */ }
                                    return (
                                        <div
                                            key={d.date}
                                            className={`aspect-square rounded-sm cursor-pointer transition-transform hover:scale-110 ${bgClass}`}
                                            onMouseEnter={() => setHoveredDay({ ...d, dateStr })}
                                            onMouseLeave={() => setHoveredDay(null)}
                                            title={`${dateStr} — ${testStatus}`}
                                            data-testid={`prediction-day-${idx}-${testStatus}`}
                                        />
                                    );
                                })}
                            </div>
                            {/* Hover detail */}
                            <div className="mt-2 min-h-[40px] text-xs">
                                {hoveredDay ? (
                                    <div className="p-2 rounded-lg bg-muted/40 border border-border" data-testid="prediction-day-detail">
                                        <p className="font-semibold">{hoveredDay.dateStr}</p>
                                        {!hoveredDay.predicted ? (
                                            <p className="text-[11px] text-muted-foreground">{t('Keine Vorhersage an diesem Tag.', 'No prediction this day.')}</p>
                                        ) : hoveredDay.warnings.length === 0 ? (
                                            <p className="text-[11px] text-muted-foreground">{t('Vorhersage gemacht — keine Warnung.', 'Prediction made — no warning.')}</p>
                                        ) : (
                                            <ul className="text-[11px] space-y-0.5">
                                                {hoveredDay.warnings.map((w, i) => (
                                                    <li key={i} className="flex items-center gap-1.5">
                                                        {w.hit === true ? (
                                                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                                        ) : w.hit === false ? (
                                                            <XCircle className="w-3 h-3 text-rose-500" />
                                                        ) : (
                                                            <MinusCircle className="w-3 h-3 text-muted-foreground" />
                                                        )}
                                                        <span>{w.symptom} ({w.conditional_pct}%, {w.lift}×)</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-[10px] text-muted-foreground/70 italic">
                                        {t('Bewege die Maus über einen Tag für Details.', 'Hover over a day for details.')}
                                    </p>
                                )}
                            </div>

                            {/* Legend */}
                            <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-muted-foreground">
                                <div className="inline-flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> {t('Treffer', 'Hit')}</div>
                                <div className="inline-flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-rose-500/70" /> {t('Verfehlt', 'Miss')}</div>
                                <div className="inline-flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-cyan-500/40" /> {t('Ausstehend', 'Pending')}</div>
                                <div className="inline-flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-amber-500/40 ring-1 ring-amber-500" /> {t('Heute', 'Today')}</div>
                                <div className="inline-flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-muted/60" /> {t('Keine', 'None')}</div>
                            </div>
                        </div>

                        {/* Learning Curve */}
                        {learning_curve && learning_curve.some(w => w.accuracy_pct != null) && (
                            <div className="pt-3 border-t border-border" data-testid="prediction-learning-curve">
                                <div className="flex items-center justify-between mb-1.5">
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                        {t('Lernkurve (Wochen-Genauigkeit)', 'Learning curve (weekly accuracy)')}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground">100%</p>
                                </div>
                                <LearningCurve weeks={learning_curve} />
                                <div className="flex justify-between text-[9px] text-muted-foreground/70 mt-0.5">
                                    {learning_curve.map((w, i) => {
                                        if (learning_curve.length > 6 && i % 2 !== 0 && i !== learning_curve.length - 1) return <span key={i} />;
                                        let lbl = w.week_start;
                                        try { lbl = format(parseISO(w.week_start), 'd. MMM', { locale }); } catch (e) { /* ignore */ }
                                        return <span key={i}>{lbl}</span>;
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Per-Metric Calibration */}
                        {per_metric_accuracy && per_metric_accuracy.length > 0 && (
                            <div className="pt-3 border-t border-border" data-testid="prediction-per-metric">
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                                    {t('Genauigkeit pro Metrik', 'Accuracy per metric')}
                                </p>
                                <div className="space-y-1.5">
                                    {per_metric_accuracy.map((m) => {
                                        const meta = METRIC_META[m.metric] || { Icon: Info, color: '#6b7280', de: m.metric, en: m.metric };
                                        const Icon = meta.Icon;
                                        const pct = m.accuracy_pct ?? 0;
                                        return (
                                            <div key={m.metric} className="flex items-center gap-2" data-testid={`per-metric-${m.metric}`}>
                                                <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: meta.color }} />
                                                <span className="text-[11px] font-medium w-16 truncate">{language === 'de' ? meta.de : meta.en}</span>
                                                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: meta.color }} />
                                                </div>
                                                <span className="text-[11px] font-semibold tabular-nums w-14 text-right">
                                                    {m.accuracy_pct != null ? `${m.accuracy_pct}%` : '—'}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground tabular-nums w-12 text-right">
                                                    {m.hits}/{m.total}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Calibration Note (Nanouk's self-reflection) */}
                        {calibration_note && (() => {
                            const bestMeta = METRIC_META[calibration_note.best_metric];
                            const worstMeta = METRIC_META[calibration_note.worst_metric];
                            const bestLbl = bestMeta ? (language === 'de' ? bestMeta.de : bestMeta.en) : calibration_note.best_metric;
                            const worstLbl = worstMeta ? (language === 'de' ? worstMeta.de : worstMeta.en) : calibration_note.worst_metric;
                            return (
                                <div className="pt-3 border-t border-border" data-testid="prediction-calibration-note">
                                    <div className="flex items-start gap-2 p-2.5 rounded-xl bg-violet-500/10 border border-violet-500/20">
                                        <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                                            <Brain className="w-3.5 h-3.5 text-violet-400" />
                                        </div>
                                        <p className="text-[11px] leading-relaxed text-foreground/85">
                                            {t(
                                                `Bei dir treffen ${bestLbl}-Vorhersagen ${calibration_note.best_accuracy_pct}% — ${worstLbl}-Vorhersagen nur ${calibration_note.worst_accuracy_pct}%. Ich werde ${worstLbl}-Warnungen vorsichtiger machen.`,
                                                `Your ${bestLbl} predictions hit ${calibration_note.best_accuracy_pct}% — ${worstLbl} only ${calibration_note.worst_accuracy_pct}%. I'll be more conservative with ${worstLbl} warnings.`
                                            )}
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
